import type { ComponentType } from 'react';
import type { ColumnDef, DatasetQueryConfig } from '@deckspace/types';
import { useSurfaceContext } from '@deckspace/ui';
import { useDataset } from '../hooks/useDataset';

interface DataBackedBaseProps {
  nodeId: string;
  sourceDataset: string;
  queryConfig?: DatasetQueryConfig;
}

interface InjectedDataProps {
  rows: Record<string, unknown>[];
  columnDefs: ColumnDef[];
}

/**
 * Higher-order component that wraps a pure data-backed View with dataset
 * fetching via `useDataset`. The wrapped component receives `rows` and
 * `columnDefs` as additional props.
 *
 * Loading and error states are rendered locally — Views never see undefined
 * data.
 */
export function withDataset<P extends DataBackedBaseProps>(
  View: ComponentType<P & InjectedDataProps>
): ComponentType<P> {
  function WithDataset(props: P) {
    const { workspaceId } = useSurfaceContext();
    const { data, isLoading, error } = useDataset(
      workspaceId,
      props.sourceDataset,
      props.queryConfig
    );

    if (error) {
      return (
        <div className="flex h-full w-full items-center justify-center text-destructive">
          <div className="p-4 text-center">
            <p className="text-sm font-medium">Failed to load data</p>
            <p className="mt-1 text-xs opacity-75">{error.message}</p>
          </div>
        </div>
      );
    }

    if (isLoading || !data) {
      return (
        <div className="flex h-full w-full items-center justify-center text-muted-foreground">
          <div className="animate-pulse text-sm">Loading…</div>
        </div>
      );
    }

    return <View {...props} rows={data.rows} columnDefs={data.columnDefs} />;
  }
  WithDataset.displayName = `withDataset(${View.displayName ?? View.name ?? 'View'})`;
  return WithDataset;
}
